/**
 * Dienste der SPA (Anmeldung, API, Datei-Upload) als React-Kontext; in Tests austauschbar.
 */
import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';
import type { MediaUploadTicket } from '../../shared/media.js';
import type { ApiClient } from './api/client.js';
import type { AuthState, AuthStore } from './auth/session.js';
import type { AuthMode } from './config.js';

/** Formular-Upload einer Datei in den Staging-Bucket (presigned POST); Fortschritt 0..1, abbrechbar über `signal`. */
export type UploadFn = (ticket: MediaUploadTicket, file: File, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<void>;

/** Statusabfrage laufender Uploads: Abstand und Höchstdauer der Verarbeitung je Datei. */
export interface UploadTiming {
  pollMs: number;
  timeoutMs: number;
}

/** `STUFE_G_PLAN.md` §4.7: alle 1,5 s, höchstens 60 s. */
export const UPLOAD_TIMING: UploadTiming = { pollMs: 1500, timeoutMs: 60_000 };

export interface Services {
  auth: AuthStore;
  api: ApiClient;
  authMode: AuthMode;
  /** Seitenwechsel zu eBay und Download der Rechnungs-PDFs (in Tests ersetzbar). */
  redirect?: (url: string) => void;
  /** Upload in den Bucket; fehlt = `xhrUpload` (in Tests ersetzbar). */
  upload?: UploadFn;
  /** Takt der Statusabfrage; fehlt = `UPLOAD_TIMING` (Tests verkürzen ihn). */
  uploadTiming?: UploadTiming;
  /**
   * EINGEFROREN 30.10.2026 (Ballast-Abbau P8): `false` öffnet die eingefrorenen Bereiche wieder (nur die Tests unter
   * `frozen/`, gegen eine API mit `unfreeze`). Fehlt = eingefroren wie in dev und prod.
   */
  frozen?: boolean;
}

/** Bucket lehnt die Übertragung ab (Status ≠ 2xx) oder das Netz bricht ab (`status` 0). */
export class UploadTransferError extends Error {
  constructor(readonly status: number) {
    super(`upload ${status}`);
    this.name = 'UploadTransferError';
  }
}

/**
 * Standard-Upload per XHR (nur so gibt es Fortschritt): alle Felder des Tickets in Reihenfolge, die Datei zuletzt als
 * `file`. Ohne Authorization-Header und ohne Cookies; der Bucket prüft allein die Signatur der Felder.
 */
export function xhrUpload(ticket: MediaUploadTicket, file: File, onProgress: (fraction: number) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Upload abgebrochen', 'AbortError'));
      return;
    }
    const form = new FormData();
    for (const [name, value] of Object.entries(ticket.fields)) form.append(name, value);
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    signal.addEventListener('abort', abort, { once: true });
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else reject(new UploadTransferError(xhr.status));
    };
    xhr.onerror = () => reject(new UploadTransferError(0));
    xhr.onabort = () => reject(new DOMException('Upload abgebrochen', 'AbortError'));
    xhr.onloadend = () => signal.removeEventListener('abort', abort);
    xhr.open('POST', ticket.url);
    xhr.send(form);
  });
}

const ServicesContext = createContext<Services | undefined>(undefined);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

/**
 * Eingefroren (Ballast-Abbau P8, Claude 30.10.2026): eBay-Verbindung, Angebote mit Vorschau, Live-Update/Pause,
 * Angebotszuordnung und Aufträge am Artikel, Rechnungen und Mitgliederliste, Galerie-Regeleditor samt Match-Vorschau.
 */
export function useFrozen(): boolean {
  return useServices().frozen !== false;
}

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('ServicesProvider fehlt');
  return services;
}

export function useAuthState(): AuthState {
  const { auth } = useServices();
  return useSyncExternalStore(auth.subscribe, auth.getState, auth.getState);
}

const LAST_TENANT_KEY = 'pat.lastTenant';
const EBAY_CONNECT_KEY = 'pat.ebayConnect';

/** Laufender eBay-Vorgang im Tab: nur der Mandant, keine SessionID. */
export function rememberEbayConnect(tenantId: string): void {
  try {
    window.sessionStorage.setItem(EBAY_CONNECT_KEY, JSON.stringify({ tenantId, startedAt: Date.now() }));
  } catch {
    /* ignorieren */
  }
}

/** Liest den laufenden Vorgang (höchstens 15 Minuten alt), ohne ihn zu löschen. */
export function peekEbayConnect(): string | undefined {
  try {
    const raw = window.sessionStorage.getItem(EBAY_CONNECT_KEY);
    const parsed = raw ? (JSON.parse(raw) as { tenantId?: unknown; startedAt?: unknown }) : undefined;
    const fresh = typeof parsed?.startedAt === 'number' && Date.now() - parsed.startedAt < 15 * 60 * 1000;
    return fresh && typeof parsed?.tenantId === 'string' && /^[1-9][0-9]{0,19}$/.test(parsed.tenantId) ? parsed.tenantId : undefined;
  } catch {
    return undefined;
  }
}

export function clearEbayConnect(): void {
  try {
    window.sessionStorage.removeItem(EBAY_CONNECT_KEY);
  } catch {
    /* ignorieren */
  }
}

export function rememberTenant(tenantId: string): void {
  try {
    window.localStorage.setItem(LAST_TENANT_KEY, tenantId);
  } catch {
    /* ignorieren */
  }
}

export function lastTenant(): string | null {
  try {
    return window.localStorage.getItem(LAST_TENANT_KEY);
  } catch {
    return null;
  }
}
